import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Layout } from './components/layout';
import { useAuth } from './lib/auth';
import { LandingPage } from './pages/landing';
import { SignInPage } from './pages/sign-in';
import { HomePage } from './pages/home';
import { EventPage } from './pages/event/event';
import { EventPosterPage } from './pages/event/poster';
import { EventAttendeesPage, EventEditorPage } from './components/event/authoring';
import { ExplorePage } from './pages/learn';
import { InvitationPage } from './pages/invitation';
import { JoinPage, JoinWithCodePage } from './pages/join';
import { AccountLayout } from './pages/account/layout';
import { AccountProfilePage } from './pages/account/profile';
import { AccountSecurityPage } from './pages/account/security';
import { AccountAppearancePage } from './pages/account/appearance';
import { NewOrganizationPage } from './pages/organizations/new';
import { OrgLayout } from './pages/organizations/layout';
import { OrgOverviewPage } from './pages/organizations/overview';
import { OrgMembersPage } from './pages/organizations/members';
import { OrgInvitesPage } from './pages/organizations/invites';
import { OrgHelpPage } from './pages/organizations/help';
import { AdminHelpPage } from './pages/admin/help';
import { HelpRequestPage, MyHelpPage } from './pages/help';
import { OrgFeedbackPage } from './pages/organizations/feedback';
import { AdminFeedbackPage } from './pages/admin/feedback';
import { OrgGroupPage, OrgGroupsPage } from './pages/organizations/groups';
import { OrgInvitePosterPage } from './pages/organizations/invite-poster';
import { OrgChildrenPage } from './pages/organizations/children';
import { NewChildOrgPage } from './pages/organizations/new-child';
import { OrgSettingsPage } from './pages/organizations/settings';
import { OrgProfilePage } from './pages/organizations/profile';
import { OrgDirectoryPage, OrgPublicProfilePage } from './pages/org-profile';
import { AdminLayout } from './pages/admin/layout';
import { AdminOverviewPage } from './pages/admin/overview';
import { AdminRequestsPage } from './pages/admin/requests';
import { AdminOrgsPage } from './pages/admin/orgs';
import { AdminNewOrgPage } from './pages/admin/new-org';
import { AdminUsersPage } from './pages/admin/users';
import { AdminReportsPage } from './pages/admin/reports';
import { AdminUserProfilePage } from './pages/admin/user-profile';
import { OrgMemberProfilePage } from './pages/organizations/member-profile';
import { AdminAuditPage } from './pages/admin/audit';
import {
  AdminLibraryBankFormPage,
  AdminLibraryBankPage,
  AdminLibraryBanksPage,
  AdminLibraryNewQuizPage,
  AdminLibraryQuizzesPage,
  AdminLibraryCoursesPage,
  AdminLibraryNewCoursePage,
  AdminLibraryEventsPage,
  AdminLibraryNewEventPage,
  AdminLibraryPage,
  AdminLibraryQuestionEditorPage,
} from './pages/admin/library';
import {
  OrgNewQuizPage,
  OrgQuestionBankFormPage,
  OrgQuestionBankPage,
  OrgQuestionBanksPage,
  OrgQuestionEditorPage,
  OrgLibraryPage,
  OrgLibraryRedirect,
  OrgQuizzesPage,
  OrgCoursesPage,
  OrgNewCoursePage,
  OrgEventsPage,
  OrgNewEventPage,
} from './pages/organizations/quizzes';
import { OrgReportsPage } from './pages/organizations/reports';
import { OrgAssignmentsPage } from './pages/organizations/assignments';
import { QuizEditorPage } from './components/quiz/authoring';
import { CourseEditorPage, LessonEditorPage } from './pages/course/authoring';
import { CoursePage, LessonPlayerPage } from './pages/course/learn';
import { CourseAssessmentPage } from './pages/course/assessment';
import { TakeQuizPage } from './pages/quiz/take';
import {
  GuideChapterPage,
  GuidePage,
  GuidePrintPage,
  GuidesPage,
  LegacyGuideRedirect,
  ResourcesPage,
} from './pages/guides';
import { VideoPage, VideosPage } from './pages/videos';
import { CoursePosterPage, PathPosterPage, QuizPosterPage } from './pages/quiz/poster';
import { AttemptResultPage } from './pages/quiz/result';
import { CertificatesPage } from './pages/certificates';
import { VerifyCertificatePage } from './pages/verify';
import { PathPage, PathsPage, PlacementPage } from './pages/paths';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return user ? children : <Navigate to="/signin" replace />;
}

/** Signed-in people land on their dashboard; everyone else sees the landing page. */
function Root() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/home" replace /> : <LandingPage />;
}

/** Old `/orgs/...` links (e.g. in earlier emails) keep working. */
function LegacyOrgRedirect() {
  const { '*': rest = '' } = useParams();
  return <Navigate to={`/organizations/${rest}`} replace />;
}

const authed = (el: React.ReactNode) => <RequireAuth>{el}</RequireAuth>;

export function App() {
  return (
    <Routes>
      {/* The landing page has its own nav and footer, full width. */}
      <Route path="/" element={<Root />} />
      {/* A guide as one page, printed to its downloadable PDF. */}
      <Route path="/guides/:guide/print" element={<GuidePrintPage />} />
      <Route element={<Layout />}>
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/home" element={authed(<HomePage />)} />
        <Route path="/learn" element={authed(<ExplorePage />)} />
        <Route path="/join" element={authed(<JoinPage />)} />
        <Route path="/join/:code" element={<JoinWithCodePage />} />
        <Route path="/invite/:token" element={<InvitationPage />} />
        <Route path="/account" element={authed(<AccountLayout />)}>
          <Route index element={<AccountProfilePage />} />
          <Route path="security" element={<AccountSecurityPage />} />
          <Route path="appearance" element={<AccountAppearancePage />} />
        </Route>
        <Route path="/certificates" element={authed(<CertificatesPage />)} />
        <Route path="/help" element={authed(<MyHelpPage />)} />
        <Route path="/help/:requestId" element={authed(<HelpRequestPage />)} />

        {/* Public: a quiz's intro (sign in to start) and certificate verification. */}
        <Route path="/c/:slug" element={<CoursePage />} />
        <Route path="/c/:slug/poster" element={<CoursePosterPage />} />
        <Route path="/e/:slug" element={<EventPage />} />
        <Route path="/e/:slug/poster" element={<EventPosterPage />} />
        <Route path="/c/:slug/assessment" element={authed(<CourseAssessmentPage />)} />
        <Route path="/c/:slug/learn/:lessonId" element={authed(<LessonPlayerPage />)} />
        <Route path="/q/:slug" element={<TakeQuizPage />} />
        <Route path="/q/:slug/poster" element={<QuizPosterPage />} />
        <Route path="/attempts/:attemptId" element={authed(<AttemptResultPage />)} />
        <Route path="/verify/:code" element={<VerifyCertificatePage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/videos/:video" element={<VideoPage />} />
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/guides/:guide" element={<GuidePage />} />
        <Route path="/guides/:guide/:chapter" element={<GuideChapterPage />} />
        <Route path="/guide" element={<LegacyGuideRedirect />} />
        <Route path="/guide/:chapter" element={<LegacyGuideRedirect />} />
        <Route path="/paths" element={<PathsPage />} />
        <Route path="/paths/start" element={<PlacementPage />} />
        <Route path="/paths/:path" element={<PathPage />} />
        <Route path="/paths/:path/poster" element={<PathPosterPage />} />
        <Route path="/o" element={<OrgDirectoryPage />} />
        <Route path="/o/:slug" element={<OrgPublicProfilePage />} />
        <Route path="/quizzes/:quizId/edit" element={authed(<QuizEditorPage />)} />
        <Route path="/courses/:courseId/edit" element={authed(<CourseEditorPage />)} />
        <Route path="/events/:eventId/edit" element={authed(<EventEditorPage />)} />
        <Route path="/events/:eventId/attendees" element={authed(<EventAttendeesPage />)} />
        <Route
          path="/courses/:courseId/lessons/:lessonId/edit"
          element={authed(<LessonEditorPage />)}
        />

        <Route path="/organizations" element={<Navigate to="/home" replace />} />
        <Route path="/organizations/new" element={authed(<NewOrganizationPage />)} />
        <Route path="/organizations/:orgId" element={authed(<OrgLayout />)}>
          <Route index element={<OrgOverviewPage />} />
          <Route path="members" element={<OrgMembersPage />} />
          <Route path="members/:userId" element={<OrgMemberProfilePage />} />
          <Route path="groups" element={<OrgGroupsPage />} />
          <Route path="feedback" element={<OrgFeedbackPage />} />
          <Route path="help" element={<OrgHelpPage />} />
          <Route path="groups/:groupId" element={<OrgGroupPage />} />
          <Route path="invites" element={<OrgInvitesPage />} />
          <Route path="invites/:codeId/poster" element={<OrgInvitePosterPage />} />
          <Route path="children" element={<OrgChildrenPage />} />
          <Route path="children/new" element={<NewChildOrgPage />} />
          <Route path="settings" element={<OrgSettingsPage />} />
          <Route path="profile" element={<OrgProfilePage />} />
          <Route path="library" element={<OrgLibraryPage />} />
          <Route path="library/courses" element={<OrgCoursesPage />} />
          <Route path="library/courses/new" element={<OrgNewCoursePage />} />
          <Route path="library/quizzes" element={<OrgQuizzesPage />} />
          <Route path="library/quizzes/new" element={<OrgNewQuizPage />} />
          <Route path="library/events" element={<OrgEventsPage />} />
          <Route path="library/events/new" element={<OrgNewEventPage />} />
          <Route path="library/question-banks" element={<OrgQuestionBanksPage />} />
          <Route path="library/question-banks/new" element={<OrgQuestionBankFormPage />} />
          <Route path="library/question-banks/:bankId" element={<OrgQuestionBankPage />} />
          <Route path="library/question-banks/:bankId/edit" element={<OrgQuestionBankFormPage />} />
          <Route
            path="library/question-banks/:bankId/questions/new"
            element={<OrgQuestionEditorPage />}
          />
          <Route
            path="library/question-banks/:bankId/questions/:questionId"
            element={<OrgQuestionEditorPage />}
          />
          {(['courses', 'quizzes', 'events', 'question-banks'] as const).map((section) => (
            <Route
              key={section}
              path={`${section}/*`}
              element={<OrgLibraryRedirect section={section} />}
            />
          ))}
          <Route path="assignments" element={<OrgAssignmentsPage />} />
          <Route path="reports" element={<OrgReportsPage />} />
        </Route>
        <Route path="/orgs/*" element={<LegacyOrgRedirect />} />

        <Route path="/admin" element={authed(<AdminLayout />)}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="requests" element={<AdminRequestsPage />} />
          <Route path="organizations" element={<AdminOrgsPage />} />
          <Route path="organizations/new" element={<AdminNewOrgPage />} />
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:userId" element={<AdminUserProfilePage />} />
          <Route path="audit" element={<AdminAuditPage />} />
          <Route path="feedback" element={<AdminFeedbackPage />} />
          <Route path="help" element={<AdminHelpPage />} />
          <Route path="library" element={<AdminLibraryPage />} />
          <Route path="library/quizzes" element={<AdminLibraryQuizzesPage />} />
          <Route path="library/quizzes/new" element={<AdminLibraryNewQuizPage />} />
          <Route path="library/courses" element={<AdminLibraryCoursesPage />} />
          <Route path="library/courses/new" element={<AdminLibraryNewCoursePage />} />
          <Route path="library/events" element={<AdminLibraryEventsPage />} />
          <Route path="library/events/new" element={<AdminLibraryNewEventPage />} />
          <Route path="library/question-banks" element={<AdminLibraryBanksPage />} />
          <Route path="library/question-banks/new" element={<AdminLibraryBankFormPage />} />
          <Route path="library/question-banks/:bankId" element={<AdminLibraryBankPage />} />
          <Route
            path="library/question-banks/:bankId/edit"
            element={<AdminLibraryBankFormPage />}
          />
          <Route
            path="library/question-banks/:bankId/questions/new"
            element={<AdminLibraryQuestionEditorPage />}
          />
          <Route
            path="library/question-banks/:bankId/questions/:questionId"
            element={<AdminLibraryQuestionEditorPage />}
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
